/**
 * requestStore.js
 * --------------------------------------------------------------------------
 * Shared in-browser store for employee information requests. Used by both:
 *   - Employee portal → /employee-portal (submits new requests)
 *   - HR admin dashboard → /analytics-dashboard (sees and approves them)
 *
 * Backed by localStorage so the request survives a page refresh during a demo,
 * and so switching between admin / employee roles preserves the state.
 *
 * In production this would be a real backend (Supabase row, audit log entry,
 * notification fanout). For the investor demo, localStorage is sufficient and
 * makes the cross-role moment feel real.
 */

const STORAGE_KEY = 'clearequal.requests.v1';
const EVENT_NAME = 'clearequal.requests.changed';

const seedRequests = (formatDate) => [
  {
    id: 'seed-completed-1',
    title: 'Vollständiger Vergütungsdaten-Export',
    titleEn: 'Complete Pay Data Export',
    description: 'Umfassende Gehalts- und Zusatzleistungsinformationen für persönliche Unterlagen',
    descriptionEn: 'Comprehensive salary and benefits information for personal records',
    status: 'completed',
    requestDate: formatDate(monthsAgo(2)),
    completedDate: formatDate(monthsAgo(1)),
    downloadUrl: '#',
    employeeName: 'Pascal Schumachers',
    employeeId: 'EMP-1042',
  },
];

function monthsAgo(n) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d;
}

function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeAll(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    // Same-tab listeners need a manual event since 'storage' only fires cross-tab
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch (err) {
    // Quota or private mode, fail silently for the demo
    console.warn('requestStore: write failed', err);
  }
}

/**
 * getRequests, returns all requests, seeding default data on first read.
 * The seed includes one historical 'completed' request so the list isn't empty
 * the first time someone opens the page during a demo.
 */
export function getRequests(formatDate) {
  let items = readAll();
  if (items === null) {
    items = formatDate ? seedRequests(formatDate) : [];
    writeAll(items);
  }
  return items;
}

/**
 * addRequest, used by employee portal. Adds a new request with status
 * 'pending' (waiting for admin approval). Returns the new ID.
 */
export function addRequest(payload) {
  const items = readAll() || [];
  const newRequest = {
    id: `req-${Date.now()}`,
    status: 'pending',
    employeeName: 'Pascal Schumachers',
    employeeId: 'EMP-1042',
    ...payload,
  };
  writeAll([newRequest, ...items]);
  return newRequest.id;
}

/**
 * approveRequest, used by HR admin dashboard. Flips a pending request to
 * 'completed' and stamps the completion date. Adds a downloadable URL.
 */
export function approveRequest(id, completedDate) {
  const items = readAll() || [];
  const updated = items.map((r) =>
    r.id === id
      ? { ...r, status: 'completed', completedDate, downloadUrl: '#', approvedBy: 'HR Admin' }
      : r
  );
  writeAll(updated);
}

/**
 * subscribe, listen for store changes. Returns an unsubscribe fn.
 * Listens to BOTH the same-tab custom event and cross-tab 'storage' event,
 * so it works whether the user has employee + admin in one tab or two tabs.
 */
export function subscribe(callback) {
  const onChange = () => callback();
  const onStorage = (e) => {
    if (e.key === STORAGE_KEY) callback();
  };
  window.addEventListener(EVENT_NAME, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVENT_NAME, onChange);
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * resetStore, debug / demo reset hook. Clears the persisted state.
 * Useful before doing a clean demo run.
 */
export function resetStore() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch {}
}
